import React, { useState, useEffect } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Chip,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  BookmarkBorder as BookmarkIcon,
  Search as SearchIcon,
  Star as StarIcon,
  Delete as DeleteIcon,
  StarBorder as StarBorderIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import savedSearchService from '../../services/savedSearchService';

/**
 * 📋 DROPDOWN RECHERCHES SAUVEGARDÉES
 * 
 * Fonctionnalités:
 * ✅ Liste des recherches sauvegardées
 * ✅ Application en un clic
 * ✅ Suppression rapide
 * ✅ Indicateur de recherche par défaut
 * ✅ Actualisation manuelle
 */
const SavedSearchDropdown = ({ 
  onApplySearch, 
  onError 
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const [savedSearches, setSavedSearches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingApply, setLoadingApply] = useState(null);
  const [error, setError] = useState(null);

  const open = Boolean(anchorEl);

  // 🔄 Charger les recherches sauvegardées
  const loadSavedSearches = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      console.log('📋 [SavedSearchDropdown] Loading saved searches');

      const result = await savedSearchService.getUserSavedSearches();
      setSavedSearches(result.data || []);

      console.log('✅ [SavedSearchDropdown] Searches loaded:', result.data?.length || 0);
      
    } catch (error) {
      console.error('❌ [SavedSearchDropdown] Load error:', error);
      setError(error.message);
      onError?.(error.message);
    } finally {
      setLoading(false);
    }
  };

  // 🎯 Charger au montage et lors de l'ouverture
  useEffect(() => {
    if (user && open) {
      loadSavedSearches();
    }
  }, [user, open]);

  // 📂 Ouvrir le menu
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // 🚪 Fermer le menu
  const handleClose = () => {
    setAnchorEl(null);
  };

  // 🔄 Appliquer une recherche
  const handleApplySearch = async (searchId, searchName) => {
    try {
      setLoadingApply(searchId);
      setError(null);

      console.log('📋 [SavedSearchDropdown] Applying search:', { searchId, searchName });

      const searchData = await savedSearchService.applySearch(searchId);
      
      console.log('✅ [SavedSearchDropdown] Search applied:', searchData);

      // Notifier le parent avec les filtres
      onApplySearch?.(searchData.filters, searchData);

      handleClose();

    } catch (error) {
      console.error('❌ [SavedSearchDropdown] Apply error:', error);
      setError(error.message);
      onError?.(error.message);
    } finally {
      setLoadingApply(null);
    }
  };

  // 🗑️ Supprimer une recherche
  const handleDeleteSearch = async (searchId, searchName, event) => {
    event.stopPropagation(); // Empêcher l'application de la recherche

    try {
      console.log('📋 [SavedSearchDropdown] Deleting search:', { searchId, searchName });

      await savedSearchService.deleteSearch(searchId);

      console.log('✅ [SavedSearchDropdown] Search deleted');

      // Recharger la liste
      await loadSavedSearches();

    } catch (error) {
      console.error('❌ [SavedSearchDropdown] Delete error:', error);
      setError(error.message);
      onError?.(error.message);
    }
  };

  // ⭐ Définir comme par défaut
  const handleSetAsDefault = async (searchId, searchName, event) => {
    event.stopPropagation();

    try {
      console.log('📋 [SavedSearchDropdown] Setting as default:', { searchId, searchName });

      await savedSearchService.setAsDefault(searchId);

      console.log('✅ [SavedSearchDropdown] Search set as default');

      // Recharger la liste
      await loadSavedSearches();

    } catch (error) {
      console.error('❌ [SavedSearchDropdown] Set default error:', error);
      setError(error.message);
      onError?.(error.message);
    }
  };

  // 🎨 Formater la date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 📊 Calculer les statistiques des filtres
  const getFiltersCount = (filters) => {
    if (!filters || typeof filters !== 'object') {
      return 0;
    }

    // Utiliser la même logique que dans SaveSearchModal
    const activeFiltersCount = Object.entries(filters).filter(([key, value]) => {
      return value !== null && value !== undefined && value !== '' && 
             !(Array.isArray(value) && value.length === 0) &&
             !key.startsWith('_'); // Exclure les métadonnées comme _activeCount, _initialSearchDone, etc.
    }).length;

    return activeFiltersCount;
  };

  if (!user) {
    return null; // Pas d'utilisateur connecté
  }

  return (
    <>
      <Button
        onClick={handleClick}
        startIcon={<BookmarkIcon />}
        variant="outlined"
        size="small"
        sx={{
          borderColor: 'rgba(98, 0, 238, 0.3)',
          color: '#6200ee',
          '&:hover': {
            borderColor: '#6200ee',
            background: 'rgba(98, 0, 238, 0.05)'
          }
        }}
      >
        {t('savedSearches.mySearches', 'Mes recherches')}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 320,
            maxHeight: 400,
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* 📋 En-tête */}
        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontSize: '1rem' }}>
              {t('savedSearches.header', 'Recherches sauvegardées')}
            </Typography>
            <Tooltip title={t('savedSearches.refresh', 'Actualiser')}>
              <IconButton 
                size="small" 
                onClick={loadSavedSearches}
                disabled={loading}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* 🔄 Chargement */}
        {loading && (
          <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {/* ❌ Erreur */}
        {error && !loading && (
          <Box sx={{ p: 2 }}>
            <Alert severity="error" size="small">
              {error}
            </Alert>
          </Box>
        )}

        {/* 📋 Liste des recherches */}
        {!loading && !error && savedSearches.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {t('savedSearches.noSavedSearches', 'Aucune recherche sauvegardée')}
            </Typography>
          </Box>
        )}

        {!loading && !error && savedSearches.length > 0 && (
          <>
            {savedSearches.map((search, index) => (
              <MenuItem
                key={search.id}
                onClick={() => handleApplySearch(search.id, search.name)}
                disabled={loadingApply === search.id}
                sx={{
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  p: 2,
                  minHeight: 60,
                  '&:hover': {
                    background: 'rgba(98, 0, 238, 0.05)'
                  }
                }}
              >
                {/* 📋 Ligne principale */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                    {loadingApply === search.id ? (
                      <CircularProgress size={16} />
                    ) : (
                      <SearchIcon fontSize="small" color="primary" />
                    )}
                    
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {search.name}
                    </Typography>
                    
                    {search.isDefault && (
                      <StarIcon fontSize="small" color="warning" />
                    )}
                  </Box>

                  {/* 🎛️ Actions */}
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {!search.isDefault && (
                      <Tooltip title={t('savedSearches.setDefault', 'Définir par défaut')}>
                        <IconButton 
                          size="small"
                          onClick={(e) => handleSetAsDefault(search.id, search.name, e)}
                        >
                          <StarBorderIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    
                    <Tooltip title={t('savedSearches.deleteSearch', 'Supprimer')}>
                      <IconButton 
                        size="small"
                        onClick={(e) => handleDeleteSearch(search.id, search.name, e)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {/* 📊 Informations */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Chip 
                    label={t('savedSearches.filtersCount_other', '{{count}} filtres', { count: getFiltersCount(search.filters) })}
                    size="small"
                    variant="outlined"
                    color="primary"
                    onClick={(e) => { e.stopPropagation(); }}
                  />
                  
                  <Typography variant="caption" color="text.secondary">
                    {search.lastUsedAt
                      ? t('savedSearches.lastUsed', 'Utilisé : {{date}}', { date: formatDate(search.lastUsedAt) })
                      : t('savedSearches.createdOn', 'Créé : {{date}}', { date: formatDate(search.createdAt) })}
                  </Typography>
                </Box>

                {/* 📝 Description */}
                {search.description && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    {search.description}
                  </Typography>
                )}
              </MenuItem>
            ))}
          </>
        )}
      </Menu>
    </>
  );
};

export default SavedSearchDropdown;
